import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import RefreshOverlay from 'Components/DesignSystem/Table/RefreshOverlay';
import Status from 'Components/DesignSystem/Status/Status';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Button from 'Components/DesignSystem/Button/Button';
import Tab from 'Components/DesignSystem/Tab/Tab';
import { ArrowDown, ArrowRight, Sort, Hashtag } from 'Components/DesignSystem/Iconography/Icons';

// Local Components
import { ActionBar, EditingBanner, RowActionsMenu, ExpandedRowContent, SearchReplaceSidePanel, ProductReplacedIndicator, LoadingOverlay, TablePaginationBar } from '../../components';
import { CreateNewProductModal, ConfirmActionModal, CreateAttributeModal, CreateAttributeValueModal, IgnoreProductModal } from '../../modals';

// API Services
import {
  getDataReviewProducts,
  replaceProduct,
  revertReplacement,
  saveBulkEditChanges,
  createProduct,
  BulkActionType,
  getDataReviewAttributeValues,
  getDataReviewAttributes,
} from 'api/DataReview';

// Utilities
import { transformToSKUMatchedRow, transformPagination, PaginationInfo, getEffectiveValues, getMergedValues } from '../../utils';
import { useBulkAction } from '../../hooks';

// Types
import {
  MPNUPCSubTabId,
  RowActionType,
  ProductAttribute,
  ProductMetadata,
  SKUMatchedRow,
  CreateProductModalData,
  IgnoreProductModalData,
} from '../../types';
import { TabItem } from 'types/DesignSystem/Tab';

// Constants
import {
  MPN_UPC_SUB_TAB_CONFIG,
  MPN_UPC_UNMAPPED_ACTIONS,
  MPN_UPC_WITH_UPDATES_ACTIONS,
  MPN_UPC_BULK_ROW_ACTIONS,
} from '../../constants';

// Icons
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <circle cx="7" cy="7" r="5" strokeWidth="1.5" />
    <path strokeLinecap="round" strokeWidth="1.5" d="M11 11l3 3" />
  </svg>
);

// View props interface
interface MPNUPCMatchedViewProps {
  channelKey: string;
  onRefresh?: () => void;
  onEditModeChange?: (editing: boolean) => void;
  onRegisterEditHandlers?: (handlers: { save: () => Promise<boolean>; cancel: () => void } | null) => void;
  subTabCounts: {
    unmappedSku: number;
    unmappedSkuWithUpdates: number;
  };
  hasUpdatePermission?: boolean;
}

// Extended row type with UI state
interface MPNUPCMatchedRowState extends SKUMatchedRow {
  isReplaced?: boolean;
  replacedWithProductId?: string;
}

// Default pagination values
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

// Sort configuration
type SortField = 'updated_at' | 'name' | 'category' | 'total_new_count' | 'total_updates_count' | 'total_removed_count';
type SortOrder = 'asc' | 'desc';

const DEFAULT_SORT_BY: SortField = 'updated_at';
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

// Column widths matching the parent table's <colgroup> — passed to ExpandedRowContent
const MPN_COL_WIDTHS = ['3%', '3%', '14%', '11%', '10%', '14%', '11%', '7%', '8%', '11%', '4%', '4%'];

/**
 * MPNUPCMatchedView Component
 *
 * Displays products matched by MPN/UPC at Industry level
 * Has two sub-tabs:
 * - Unmapped SKU: Products with no SKU mapping
 * - Unmapped SKU with Updates: Products with updates and no SKU mapping
 *
 * Uses same unified table structure as SKUMatchedView
 */

/**
 * The three change counters — New / Update / Removed — as DS `Status` (§ 67).
 *
 * They were six components (three here, three byte-identical in the sibling view),
 * each hand-painting `bg-*-100 / border-*-500`. `Status` is the primitive for exactly
 * this: a chip that REPORTS a state, with the tone pairs measured against their own
 * tint and `data-tone` on the chip so a spec never has to assert a colour class.
 *
 * They also declared `attributes` and `tooltipId` props, documented as driving a
 * Figma-specified hover tooltip — and destructured neither. **The tooltip does not
 * exist**; the props were threaded from every call site for nothing, so they are gone
 * rather than carried forward.
 *
 * No tone glyph here, deliberately, unlike the diff chips inside an expanded row: each
 * counter sits under its own column heading ("New", "Update", "Removed"), so the
 * meaning is already carried in text beside it and SC 1.4.1 is met without adding a
 * mark to every cell of three columns.
 */
const COUNTER_TONE = {
  new: 'info',
  update: 'warning',
  removed: 'danger',
} as const;

const MPNUPCMatchedView: React.FC<MPNUPCMatchedViewProps> = ({ channelKey, onRefresh, onEditModeChange, onRegisterEditHandlers, subTabCounts, hasUpdatePermission = true }) => {
  // State
  const [activeSubTab, setActiveSubTab] = useState<MPNUPCSubTabId>('unmapped-sku');
  const [searchValue, setSearchValue] = useState('');
  // Debounced mirror of searchValue. The data fetch keys off this (not the raw
  // value) so typing fires ONE request after 300ms instead of one per keystroke
  // (search=c, search=co, search=col, ...). The input stays bound to searchValue
  // for instant feedback. Mirrors the debounce already used in MissingMappingView.
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchValue(searchValue.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchValue]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [rows, setRows] = useState<MPNUPCMatchedRowState[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  // Loading flag for the Replace flow lives on the side panel —
  // feedback shows inside the panel that initiated the action rather
  // than as a page-level LoadingOverlay flashing across the view.
  // (CreateNewProductModal already manages its own `isSubmitting`
  // internally; no parent-driven flag needed there.)
  const [isReplacingProduct, setIsReplacingProduct] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
    totalPages: 0,
  });
  const [searchReplacePanel, setSearchReplacePanel] = useState<{
    isOpen: boolean;
    rowIndex: number | null;
  }>({ isOpen: false, rowIndex: null });
  const [createProductModal, setCreateProductModal] = useState<{
    isOpen: boolean;
    rowIndex: number | null;
  }>({ isOpen: false, rowIndex: null });
  // Track edited rows for edit mode
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set());

  // "Delete attribute?" Save validation popup (ticket 11443).
  const [deleteAttrConfirm, setDeleteAttrConfirm] = useState<{
    isOpen: boolean;
    names: string[];
    onConfirm: () => void;
  }>({ isOpen: false, names: [], onConfirm: () => {} });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: '', confirmLabel: 'Confirm', onConfirm: () => {} });

  // Ignore flow uses the reason-chip modal (parity with Unmatched +
  // SKU Matched). Tracks both single-row (one SKU) and bulk (multiple)
  // dispatches in the same list state.
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [pendingIgnoreSkus, setPendingIgnoreSkus] = useState<string[]>([]);

  // Edit-mode inline-create modals: opened from the dropdowns' "Create" link.
  // `rowIndex` + `attributeName` / `attrIndex` carry the context needed to
  // splice the created entity back into the row on success.
  const [createValueModal, setCreateValueModal] = useState<{
    rowIndex: number;
    attributeName: string;
  } | null>(null);
  const [createAttributeModal, setCreateAttributeModal] = useState<{
    rowIndex: number;
    attrIndex: number;
  } | null>(null);

  const showConfirm = (message: string, onConfirm: () => void, confirmLabel = 'Confirm') => {
    setConfirmModal({ isOpen: true, message, confirmLabel, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    // Clear selection on confirm-done AND cancel so a stale multi-select
    // doesn't linger after the user has started a flow.
    clearSelection();
  };

  // Get active actions based on sub-tab
  const activeActions = activeSubTab === 'unmapped-sku' ? MPN_UPC_UNMAPPED_ACTIONS : MPN_UPC_WITH_UPDATES_ACTIONS;

  // Computed values
  const selectedCount = rows.filter((r) => r.isSelected).length;

  // Build sub-tab items
  const subTabs: TabItem[] = MPN_UPC_SUB_TAB_CONFIG.map((tab) => ({
    id: tab.id,
    label: tab.label,
    badge: tab.id === 'unmapped-sku' ? subTabCounts.unmappedSku : subTabCounts.unmappedSkuWithUpdates,
  }));

  /**
   * Fetch products from API
   */
  // Tracks whether the component is still mounted. Guards setState calls
  // inside async fetchProducts so a fetch that resolves after the user
  // switched tabs (component unmounted) doesn't fire a "state update on
  // unmounted component" warning / memory leak.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!channelKey) return;

    setIsLoading(true);
    try {
      const subTabApiName = activeSubTab === 'unmapped-sku' ? 'unmapped_sku' : 'unmapped_sku_with_updates';

      const response = await getDataReviewProducts(channelKey, {
        tab: 'mpn_upc_matched',
        sub_tab: subTabApiName,
        page: pagination.page,
        per_page: pagination.perPage,
        search: debouncedSearchValue || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (!isMountedRef.current) return;

      if (response.done && response.data) {
        // Transform API data to frontend format
        const transformedRows = response.data.map((product) => ({
          ...transformToSKUMatchedRow(product),
          isReplaced: false,
          replacedWithProductId: undefined,
        }));
        // Preserve expanded state from previous rows
        setRows((prevRows) => {
          const expandedIds = new Set(prevRows.filter((r) => r.isExpanded).map((r) => r.id));
          return transformedRows.map((row) => ({
            ...row,
            isExpanded: expandedIds.has(row.id),
          }));
        });
        setPagination(transformPagination(response.page));
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch MPN/UPC matched products:', error);
      toast.error('Failed to load MPN/UPC matched products');
      setRows([]);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [channelKey, activeSubTab, pagination.page, pagination.perPage, debouncedSearchValue, sortBy, sortOrder]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Refresh after an action clears the search and resets pagination so the
  // user sees the full list (the acted-on row may have moved to another tab
  // and would be hidden by a stale search filter).
  // See the note in SKUMatchedView.refreshAfterAction — calling fetchProducts
  // directly races with the useEffect-driven refetch (stale closure wins
  // intermittently), so we only fire it when state didn't change.
  // Clear select-all + per-row selection state.
  const clearSelection = () => {
    setSelectAll(false);
    setRows((prev) => prev.map((r) => (r.isSelected ? { ...r, isSelected: false } : r)));
  };

  const refreshAfterAction = () => {
    const searchWasSet = searchValue !== '';
    const pageWasAtDefault = pagination.page === DEFAULT_PAGE;
    setSearchValue('');
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    if (!searchWasSet && pageWasAtDefault) {
      fetchProducts();
    }
    clearSelection();
    onRefresh?.();
  };

  // Hooks
  const { isBulkActionLoading, executeBulkAction } = useBulkAction({ channelKey, sourceTab: 'mpn_upc_matched', onTaskComplete: () => { refreshAfterAction(); } });

  // Handlers
  const handleSubTabChange = (tabId: string | number) => {
    if (isEditMode) {
      toast.warning('Please save or cancel your edits before switching tabs');
      return;
    }
    setActiveSubTab(tabId as MPNUPCSubTabId);
    setSelectAll(false);
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const executeBulkWithRefresh = async (skus: string[], action: string, actionData?: Record<string, any>) => {
    const success = await executeBulkAction(skus, action as BulkActionType, actionData);
    if (success) {
      refreshAfterAction();
    }
  };

  const BULK_CONFIRM_MESSAGES: Record<string, string> = {
    'map_sku': 'Are you sure you want to map {N} selected SKU(s)?',
    'map_override': 'Are you sure you want to map and overwrite {N} selected product(s)?',
    // 'ignore' is no longer a confirm-dialog action — it routes through
    // IgnoreProductModal so the reviewer can pick a reason chip or type
    // a custom comment (parity with Unmatched + SKU Matched).
    'create_new': 'Are you sure you want to create {N} new product(s)?',
  };

  const handleBulkAction = async (action: string) => {
    const selectedSkus = rows.filter((r) => r.isSelected).map((r) => r.imported.sku).filter(Boolean) as string[];
    if (selectedSkus.length === 0) {
      toast.error('No products selected');
      return;
    }

    // Ignore goes through the reason-chip modal (parity with Unmatched +
    // SKU Matched). Other actions stay on the Are-you-sure confirm flow.
    if (action === 'ignore') {
      setPendingIgnoreSkus(selectedSkus);
      setShowIgnoreModal(true);
      return;
    }

    const msgTemplate = BULK_CONFIRM_MESSAGES[action] || `Are you sure you want to ${action.replace(/_/g, ' ')} ${selectedSkus.length} selected product(s)?`;
    const message = msgTemplate.replace('{N}', String(selectedSkus.length));
    const confirmLabel = action === 'create_new' ? 'Create' : 'Confirm';

    showConfirm(message, () => { closeConfirm(); executeBulkWithRefresh(selectedSkus, action); }, confirmLabel);
  };

  const handleIgnoreProductSubmit = async (data: IgnoreProductModalData) => {
    if (pendingIgnoreSkus.length === 0) return;
    const success = await executeBulkAction(pendingIgnoreSkus, 'ignore', { reason: data.reason });
    if (success) {
      setShowIgnoreModal(false);
      setPendingIgnoreSkus([]);
      clearSelection();
      refreshAfterAction();
    }
  };

  const handleFilterClick = () => {
    // TODO: Implement filter functionality
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const renderSortIcon = (field: SortField) => {
    if (sortBy === field) {
      return (
        <span className={`tw-text-accent ${sortOrder === 'asc' ? 'tw-rotate-180' : ''} tw-inline-block tw-transition-transform`}>
          <ArrowDown />
        </span>
      );
    }
    return <span className="tw-text-ink-tertiary"><Sort /></span>;
  };

  const handleEditToggle = () => {
    const next = !isEditMode;
    setIsEditMode(next);
    onEditModeChange?.(next);
    if (next) {
      // Clear any row/select-all selections when entering edit mode — they
      // apply to bulk actions, which are hidden in edit mode.
      setSelectAll(false);
      setRows((prev) => prev.map((r) => ({ ...r, isSelected: false })));
      onRegisterEditHandlers?.({ save: () => saveRef.current(), cancel: () => cancelRef.current() });
    } else {
      onRegisterEditHandlers?.(null);
    }
  };

  // Promise resolver held while the "Delete attribute?" popup is open so we
  // can finish the deferred Save when the user confirms or cancels.
  const pendingSaveResolveRef = useRef<((ok: boolean) => void) | null>(null);

  // Build the API payload and commit to backend — split out of handleSaveChanges
  // so the deferred path (after the delete-attribute confirm popup) can call
  // the same persistence flow without re-validating.
  const performSave = async (): Promise<boolean> => {
    // Validate PLS is not blank for edited rows
    const invalidPlsRows = rows.filter(
      (row) => editedRows.has(String(row.id)) && (!row.imported.pls || row.imported.pls.trim() === '')
    );
    if (invalidPlsRows.length > 0) {
      toast.error(`PLS is required for: ${invalidPlsRows.map((r) => r.imported.product || r.imported.sku).join(', ')}`);
      return false;
    }

    // Build edits from edited rows - backend expects source_data structure
    const edits = rows
      .filter((row) => editedRows.has(String(row.id)))
      .map((row) => ({
        sku: row.imported.sku || '',
        source_data: {
          prod: {
            name: row.imported.product,
            description: row.imported.description,
            price: row.imported.price,
            upc: row.imported.upc,
            model_number: row.imported.modelNumber,
            product_lifecycle_status: row.imported.pls,
            version: row.imported.version,
          },
          parent_id: row.imported.parentId,
          category: row.imported.category,
          real_sku: row.imported.realSku || '',
          category_id: row.imported.categoryId || '',
          attributes: row.attributes
            ?.filter((attr) => {
              // Existing implicit-delete: edited values cleared → drop.
              if (attr.editedValues !== undefined && attr.editedValues.length === 0) return false;
              // Pending-new placeholder with unset/temp name → drop (the
              // delete-attribute confirm popup has already informed the user).
              if (attr.isPendingNew && (!attr.name?.trim() || attr.name.startsWith('__pending_'))) return false;
              return true;
            })
            .map((attr) => {
              const catalogAttr = row.catalogAttributes?.find((ca) => ca.name === attr.name);
              const finalValues = attr.editedValues ?? getEffectiveValues(attr, catalogAttr);
              return { name: attr.name, values: finalValues };
            }) || [],
        },
      }))
      .filter((edit) => edit.sku);

    if (edits.length === 0) {
      toast.info('No changes to save');
      setIsEditMode(false);
      onEditModeChange?.(false);
      onRegisterEditHandlers?.(null);
      // Clear the header select-all flag — matches the Unmatched / SKU
      // Matched fix so it doesn't linger as ticked after leaving edit mode.
      setSelectAll(false);
      return true;
    }

    setIsSaveLoading(true);
    try {
      const response = await saveBulkEditChanges(channelKey, { edits });

      if (response.done) {
        toast.success(`Successfully saved ${response.success_count} edit(s)`);
        if (response.failed_count > 0) {
          toast.warning(`Failed to save ${response.failed_count} edit(s)`);
        }
        setIsEditMode(false);
        onEditModeChange?.(false);
        onRegisterEditHandlers?.(null);
        setEditedRows(new Set());
        setSelectAll(false);
        // Edited rows sort to the top (updated_at desc) so page 1 is where
        // the user will see their changes. Reset to page 1 if we're not
        // already there; state change drives the refetch via useEffect.
        const pageWasAtDefault = pagination.page === DEFAULT_PAGE;
        if (!pageWasAtDefault) {
          setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
        } else {
          fetchProducts();
        }
        if (onRefresh) onRefresh();
        return true;
      } else {
        toast.error(response.message || 'Failed to save changes');
        return false;
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save changes');
      return false;
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleSaveChanges = async (): Promise<boolean> => {
    // 11443: scan edited rows for incomplete pending-new attributes. A pending
    // row counts as incomplete when its name is still the temp placeholder OR
    // its editedValues array is empty. The consolidated confirm popup tells
    // the user which attributes will be dropped (per AC wording).
    const incompletePendingLabels: string[] = [];
    rows.forEach((row) => {
      if (!editedRows.has(String(row.id))) return;
      (row.attributes || []).forEach((attr) => {
        if (!attr.isPendingNew) return;
        const hasName = !!attr.name?.trim() && !attr.name.startsWith('__pending_');
        const hasValues = (attr.editedValues?.length ?? 0) > 0;
        if (!hasName || !hasValues) {
          incompletePendingLabels.push(hasName ? attr.name : '(new attribute)');
        }
      });
    });

    if (incompletePendingLabels.length === 0) {
      return performSave();
    }

    // Defer the actual save until the user confirms (or cancels) the
    // delete-attribute popup.
    return new Promise<boolean>((resolve) => {
      pendingSaveResolveRef.current = resolve;
      setDeleteAttrConfirm({
        isOpen: true,
        names: incompletePendingLabels,
        onConfirm: async () => {
          setDeleteAttrConfirm((p) => ({ ...p, isOpen: false }));
          const ok = await performSave();
          if (pendingSaveResolveRef.current) {
            pendingSaveResolveRef.current(ok);
            pendingSaveResolveRef.current = null;
          }
        },
      });
    });
  };

  const closeDeleteAttrConfirm = () => {
    setDeleteAttrConfirm((p) => ({ ...p, isOpen: false }));
    if (pendingSaveResolveRef.current) {
      pendingSaveResolveRef.current(false);
      pendingSaveResolveRef.current = null;
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    onEditModeChange?.(false);
    onRegisterEditHandlers?.(null);
    setEditedRows(new Set());
    // Clear header select-all along with edit state — same fix as
    // Unmatched and SKU Matched.
    setSelectAll(false);
    // Re-fetch to restore original data
    fetchProducts();
  };

  // Refs to keep handlers current for AppContext registration
  const saveRef = useRef(handleSaveChanges);
  saveRef.current = handleSaveChanges;
  const cancelRef = useRef(handleCancelEdit);
  cancelRef.current = handleCancelEdit;

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setRows((prev) => prev.map((row) => ({ ...row, isSelected: checked })));
  };

  const handleRowSelect = (index: number, selected: boolean) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isSelected: selected };
      setSelectAll(updated.every((r) => r.isSelected));
      return updated;
    });
  };

  const handleRowExpand = (index: number) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isExpanded: !updated[index].isExpanded };
      return updated;
    });
  };

  const ROW_CONFIRM_MESSAGES: Record<string, string> = {
    'map-sku': 'Are you sure you want to map this SKU?',
    'map-overwrite': 'Are you sure you want to map and overwrite?',
    'map': 'Are you sure you want to map this SKU without applying updates?',
    // 'ignore' routes through IgnoreProductModal — see handleRowAction.
    'create_new': 'Are you sure you want to create this as a new product?',
  };

  // Frontend row-action types are hyphenated for the menu config; the backend
  // accepts underscore forms. Translate before dispatching.
  // 'map' (no-overwrite variant in the Unmapped-SKU-with-Updates sub-tab) maps
  // to backend 'map_sku' — linking the SKU without applying the updates.
  const ROW_ACTION_TO_BACKEND: Record<string, BulkActionType> = {
    'map-sku': 'map_sku' as BulkActionType,
    'map-overwrite': 'map_override' as BulkActionType,
    'map': 'map_sku' as BulkActionType,
    'create-new': 'create_new' as BulkActionType,
  };

  const handleRowAction = async (index: number, actionType: RowActionType) => {
    const sku = rows[index]?.imported?.sku;
    if (!sku) return;

    // Same reason-chip route for single-row Ignore as for the bulk path.
    if ((actionType as string) === 'ignore') {
      setPendingIgnoreSkus([sku]);
      setShowIgnoreModal(true);
      return;
    }

    const message = ROW_CONFIRM_MESSAGES[actionType] || `Are you sure you want to ${actionType.replace(/_/g, ' ')} this product?`;
    const confirmLabel = (actionType as string) === 'create_new' ? 'Create' : 'Confirm';
    const backendAction = ROW_ACTION_TO_BACKEND[actionType] || (actionType as unknown as BulkActionType);

    showConfirm(message, () => { closeConfirm(); executeBulkWithRefresh([sku], backendAction); }, confirmLabel);
  };

  const handleSearchReplace = (index: number) => {
    setSearchReplacePanel({ isOpen: true, rowIndex: index });
  };

  const handleCloseSidePanel = () => {
    setSearchReplacePanel({ isOpen: false, rowIndex: null });
  };

  // CreateNewProductModal handlers retained for future reuse — the
  // expanded-row "+ Create as New Product" button was removed in ticket 11444
  // but the modal itself remains importable from other entry points.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCreateNewProduct = (index: number) => {
    setCreateProductModal({ isOpen: true, rowIndex: index });
  };

  const handleCloseCreateProductModal = () => {
    setCreateProductModal({ isOpen: false, rowIndex: null });
  };

  const handleSubmitCreateProduct = async (data: CreateProductModalData) => {
    if (!channelKey || createProductModal.rowIndex === null) return;

    const row = rows[createProductModal.rowIndex];
    const sku = row.imported.sku;

    if (!sku) {
      toast.error('Cannot create product: SKU is missing');
      return;
    }

    if (!data.categoryUid) {
      toast.error('Cannot create product: Category UID is missing');
      return;
    }

    // CreateNewProductModal manages its own `isSubmitting` while
    // awaiting this onSubmit handler — no page-level LoadingOverlay
    // needed.
    try {
      const parsedPrice = data.price ? parseFloat(data.price) : undefined;
      const response = await createProduct(channelKey, sku, {
        product: {
          name: data.productName,
          model_number: data.modelNumber,
          product_lifecycle_status: data.pls || row.imported.pls || 'Enabled',
          description: data.description || row.imported.description || '',
          version: data.version || row.imported.version || String(new Date().getFullYear()),
          upc: data.upc || '',
          price: parsedPrice !== undefined && !isNaN(parsedPrice) ? parsedPrice : undefined,
        },
        category_uid: data.categoryUid,
        parent_id: data.parentId || row.imported.parentId || '',
        real_sku: row.imported.realSku || '',
        attributes: data.attributes
          .filter((attr) => attr.name && attr.values && attr.values.length > 0)
          .map((attr) => ({
            name: attr.name,
            values: attr.values,
          })),
      });

      if (response.done) {
        toast.success(response.message || 'Product created successfully');
        handleCloseCreateProductModal();
        // Refresh data after creating — the row moves to Processed, so clear
        // the search filter and reset pagination.
        refreshAfterAction();
      } else {
        toast.error(response.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error('Failed to create product');
    }
  };

  const handleAttributeChange = (rowIndex: number, attrIndex: number, value: string) => {
    setRows((prev) => {
      const updated = [...prev];
      if (updated[rowIndex].attributes) {
        const attributes = [...updated[rowIndex].attributes!];
        attributes[attrIndex] = { ...attributes[attrIndex], value };
        updated[rowIndex] = { ...updated[rowIndex], attributes };
        // Track this row as edited
        setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      }
      return updated;
    });
  };

  const handleDeleteAttribute = (rowIndex: number, attrIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      if (updated[rowIndex].attributes) {
        const attributes = [...updated[rowIndex].attributes!];
        attributes[attrIndex] = { ...attributes[attrIndex], editedValues: [] };
        updated[rowIndex] = { ...updated[rowIndex], attributes };
        setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      }
      return updated;
    });
  };

  const handleRevertAttribute = (rowIndex: number, attrIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { editedValues: _, ...rest } = attrs[attrIndex];
      attrs[attrIndex] = rest;
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      return updated;
    });
  };

  const handleDeleteAttributeValue = (rowIndex: number, attrIndex: number, valueIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attr = updated[rowIndex].attributes?.[attrIndex];
      if (!attr) return prev;
      const catalogAttr = updated[rowIndex].catalogAttributes?.find((ca) => ca.name === attr.name);
      const current = attr.editedValues ?? getEffectiveValues(attr, catalogAttr);
      const valToRemove = getMergedValues(attr)[valueIndex];
      const newEdited = current.filter((v) => v !== valToRemove);
      updated[rowIndex] = {
        ...updated[rowIndex],
        attributes: updated[rowIndex].attributes!.map((a, i) =>
          i === attrIndex ? { ...a, editedValues: newEdited } : a
        ),
      };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleRevertAttributeValue = (rowIndex: number, attrIndex: number, valueIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attr = attrs[attrIndex];
      const valToRestore = getMergedValues(attr)[valueIndex];
      const current = attr.editedValues ?? [];
      if (!current.includes(valToRestore)) {
        attrs[attrIndex] = { ...attr, editedValues: [...current, valToRestore] };
        updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      }
      return updated;
    });
  };

  const handleRestoreAttributeValue = (rowIndex: number, attrIndex: number, valueIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attr = attrs[attrIndex];
      const catalogAttr = updated[rowIndex].catalogAttributes?.find((ca) => ca.name === attr.name);
      const valToRestore = getMergedValues(attr)[valueIndex];
      const current = attr.editedValues ?? getEffectiveValues(attr, catalogAttr);
      if (!current.includes(valToRestore)) {
        attrs[attrIndex] = { ...attr, editedValues: [...current, valToRestore] };
      }
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleUndoRestoreAttributeValue = (rowIndex: number, attrIndex: number, valueIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attr = attrs[attrIndex];
      const catalogAttr = updated[rowIndex].catalogAttributes?.find((ca) => ca.name === attr.name);
      const valToRemove = getMergedValues(attr)[valueIndex];
      const current = attr.editedValues ?? getEffectiveValues(attr, catalogAttr);
      attrs[attrIndex] = { ...attr, editedValues: current.filter((v) => v !== valToRemove) };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // Append a value to an existing attribute's editedValues (inline + dropdown).
  // The dropdown is multi-select with apply-per-check, so a click adds one
  // value at a time.
  const handleAddAttributeValue = (rowIndex: number, attrIndex: number, valueToAdd: string) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attr = attrs[attrIndex];
      if (!attr) return prev;
      const catalogAttr = updated[rowIndex].catalogAttributes?.find((ca) => ca.name === attr.name);
      const current = attr.editedValues ?? getEffectiveValues(attr, catalogAttr);
      if (current.includes(valueToAdd)) return prev;
      attrs[attrIndex] = { ...attr, editedValues: [...current, valueToAdd] };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // Add a brand-new placeholder attribute row (Add Attribute button). The
  // temporary name keeps getMatchedRows' Set dedupe from collapsing multiple
  // placeholders into one row before the user picks a real name.
  const handleAddNewAttribute = (rowIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const tempName = `__pending_${Date.now()}_${Math.random().toString(36).slice(2, 7)}__`;
      attrs.push({
        name: tempName,
        value: '',
        values: [],
        isPendingNew: true,
        editedValues: [],
      });
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // User picked a name for the pending-new row — swap the temp name for the
  // real one so the value loader can resolve the attribute id.
  const handleSelectNewAttributeName = (rowIndex: number, attrIndex: number, name: string) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      if (!attrs[attrIndex]) return prev;
      attrs[attrIndex] = { ...attrs[attrIndex], name };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // Per-row attribute loader — see UnmatchedView.tsx for the full rationale.
  // Industry-scoped via `GET /catalogue/data-review/<cs_key>/attributes`.
  const loadAttributeOptionsForRow = useCallback(
    (rowIndex: number) => async (search: string, page: number) => {
      if (!channelKey) return { options: [], hasMore: false };
      const PAGE_SIZE = 20;
      try {
        const res = await getDataReviewAttributes(channelKey, page, PAGE_SIZE, search);
        if (!res?.done || !res.data) return { options: [], hasMore: false };
        const existing = new Set(
          (rows[rowIndex]?.attributes || [])
            .map((a) => a.name?.toLowerCase())
            .filter((n): n is string => !!n && !n.startsWith('__pending_'))
        );
        return {
          options: (res.data.items || [])
            .map((it) => ({ value: it.name, label: it.name }))
            .filter((o) => !existing.has(o.value.toLowerCase())),
          hasMore: !!res.data.pagination?.has_next,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [channelKey, rows]
  );

  // Catalog AV loader — see UnmatchedView.tsx for the full rationale.
  // Single-call: `GET /catalogue/data-review/:cs_key/attribute-values?
  // attribute=:name&...` returns Neo4j catalog AVs scoped to the channel's
  // industry. Reads the same subgraph that `Create New Value` writes to,
  // so newly-created values appear immediately.
  const loadValueOptionsForRow = useCallback(
    (_rowIndex: number) => (attributeName: string) => async (search: string, page: number) => {
      if (!attributeName || attributeName.startsWith('__pending_')) {
        return { options: [], hasMore: false };
      }
      if (!channelKey) return { options: [], hasMore: false };
      const PAGE_SIZE = 20;
      try {
        const res = await getDataReviewAttributeValues(
          channelKey,
          attributeName,
          page,
          PAGE_SIZE,
          search,
        );
        if (!res?.done || !res.data) return { options: [], hasMore: false };
        const items = res.data.items || [];
        return {
          options: items
            .map((it) => (it?.value ?? '').toString())
            .filter((v) => v.length > 0)
            .map((v) => ({ value: v, label: v })),
          hasMore: !!res.data.pagination?.has_next,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [channelKey]
  );

  const handleModalAttrValuesSave = (
    rowIndex: number,
    attrName: string,
    valuesToKeep: string[]
  ) => {
    if (!isEditMode) {
      setIsEditMode(true);
      onEditModeChange?.(true);
      onRegisterEditHandlers?.({ save: () => saveRef.current(), cancel: () => cancelRef.current() });
    }
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attrIndex = attrs.findIndex((a) => a.name === attrName);
      if (attrIndex === -1) return prev;

      attrs[attrIndex] = { ...attrs[attrIndex], editedValues: valuesToKeep };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleMetadataChange = (
    rowIndex: number,
    field: keyof ProductMetadata,
    value: string
  ) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        imported: {
          ...updated[rowIndex].imported,
          [field]: value,
        },
      };
      // Track this row as edited
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleReplaceProduct = async (productId: string) => {
    if (searchReplacePanel.rowIndex === null || !channelKey) return;

    const row = rows[searchReplacePanel.rowIndex];
    const sku = row.imported.sku;

    if (!sku) {
      toast.error('Cannot replace product: SKU is missing');
      return;
    }

    // Loading state lives on the side panel (passed via `isReplacing`),
    // not the page-level LoadingOverlay. The user just clicked Replace
    // inside the panel — feedback should appear *inside* that panel,
    // not as a separate overlay flashing across the whole view.
    setIsReplacingProduct(true);
    try {
      const response = await replaceProduct(channelKey, sku, {
        new_gms_id: productId,
        reason: 'Manual replacement via Search & Replace',
      });

      if (response.done) {
        setRows((prev) =>
          prev.map((item, i) =>
            i === searchReplacePanel.rowIndex
              ? { ...item, isReplaced: true, replacedWithProductId: productId, isExpanded: false }
              : item
          )
        );
        setSearchReplacePanel({ isOpen: false, rowIndex: null });
        toast.success('Product replaced successfully');
      } else {
        toast.error(response.message || 'Failed to replace product');
      }
    } catch (error) {
      console.error('Failed to replace product:', error);
      toast.error('Failed to replace product');
    } finally {
      setIsReplacingProduct(false);
    }
  };

  const handleUndoReplace = async (index: number) => {
    if (!channelKey) return;

    const row = rows[index];
    const sku = row.imported.sku;

    if (!sku) {
      toast.error('Cannot revert replacement: SKU is missing');
      return;
    }

    // No page-level LoadingOverlay — see handleReplaceProduct above
    // for the rationale. Toast on completion handles user feedback.
    try {
      const response = await revertReplacement(channelKey, sku);

      if (response.done) {
        setRows((prev) =>
          prev.map((item, i) =>
            i === index
              ? { ...item, isReplaced: false, replacedWithProductId: undefined }
              : item
          )
        );
        toast.success('Replacement reverted successfully');
      } else {
        toast.error(response.message || 'Failed to revert replacement');
      }
    } catch (error) {
      console.error('Failed to revert replacement:', error);
      toast.error('Failed to revert replacement');
    }
  };

  const handlePageChange = (newPage: number) => {
    clearSelection();
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPerPage: string | number) => {
    clearSelection();
    setPagination((prev) => ({ ...prev, perPage: newPerPage as number, page: DEFAULT_PAGE }));
  };

  return (
    <div className="tw-space-y-4 tw-relative">
      {/* Sub-Tab Navigation */}
      <Tab
        tabs={subTabs}
        activeTabId={activeSubTab}
        onChange={handleSubTabChange}
        showIcons={false}
      />

      {/* Action Bar */}
      <ActionBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        bulkActions={MPN_UPC_BULK_ROW_ACTIONS}
        onBulkAction={handleBulkAction}
        onFilterClick={handleFilterClick}
        isEditMode={isEditMode}
        onEditToggle={handleEditToggle}
        selectedCount={selectedCount}
        isLoading={isBulkActionLoading || isSaveLoading}
        hasUpdatePermission={hasUpdatePermission}
      />

      {/* Editing Banner */}
      {isEditMode && (
        <EditingBanner onCancel={handleCancelEdit} onSave={handleSaveChanges} />
      )}

      {/* Single Unified Table with Color-Coded Columns (same as SKUMatchedView) */}
      {(
        <div className="tw-relative tw-border tw-border-edge tw-rounded tw-flex tw-flex-col" style={{ maxHeight: 'calc(100vh - 320px)', minHeight: '400px' }}>
          {/*
            The "Refreshing" chip Guide/ISR shows on the same gesture. These
            tables are hand-rolled rather than the DS `Table`, so they got the
            dim without the chip — the rows faded and nothing said why. Shares
            `Table`'s implementation rather than restating it.

            `rows.length > 0` is the whole condition: a COLD load has nothing to
            dim and shows its spinner row instead, and drawing both would put
            "Refreshing" on top of "Loading...".
          */}
          <RefreshOverlay
            active={isLoading && rows.length > 0}
            data-testid="catalog-table-refreshing-mpn-upc-matched"
          />
          {/* Header — does not scroll. flex-shrink-0 pins it at natural height;
              overflow:hidden creates a scroll container so
              scrollbar-gutter:stable actually reserves right-side space,
              keeping column edges aligned with the body table below. */}
          <div className="tw-flex-shrink-0" style={{ overflow: 'hidden', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            {/* Column Groups for Width Definitions */}
            {/* Catalog: Checkbox | # | Product | Category | Attributes || Imported: Product | Category | New | Update | Removed | Actions | Expand */}
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '4%' }} />
              <col style={{ width: '4%' }} />
            </colgroup>

            {/* Table Header - Two Rows */}
            <thead className="tw-bg-surface">
              {/* Section Headers Row */}
              <tr className="tw-bg-surface">
                <th
                  colSpan={5}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink-secondary">
                    Product Catalog
                  </Typography>
                </th>
                <th
                  colSpan={7}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-l tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink">
                    Imported Products
                  </Typography>
                </th>
              </tr>

              {/* Column Headers Row */}
              <tr className="tw-border-b tw-border-edge tw-bg-surface">
                {/* Product Catalog Headers */}
                <th className="tw-px-2 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken">
                  <Checkbox
                    label=""
                    checked={selectAll}
                    indeterminate={selectedCount > 0 && selectedCount < rows.length}
                    onChange={handleSelectAll}
                    disabled={isEditMode}
                  />
                </th>
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-surface-sunken">
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                    <Hashtag />
                  </span>
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken">
                  <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography></span>
                  </span>
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken">
                  <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography></span>
                  </span>
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-surface-sunken">
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Attributes
                    </Typography></span>
                  </span>
                </th>

                {/* Imported Products Headers - grey-200 bg (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-border-l tw-border-edge tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('name')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography></span>
                    <span className="tw-flex-shrink-0">{renderSortIcon('name')}</span>
                  </span>
                </th>
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('category')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography></span>
                    <span className="tw-flex-shrink-0">{renderSortIcon('category')}</span>
                  </span>
                </th>
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('total_new_count')}
                >
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      New
                    </Typography></span>
                    <span className="tw-flex-shrink-0">{renderSortIcon('total_new_count')}</span>
                  </span>
                </th>
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('total_updates_count')}
                >
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Update
                    </Typography></span>
                    <span className="tw-flex-shrink-0">{renderSortIcon('total_updates_count')}</span>
                  </span>
                </th>
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('total_removed_count')}
                >
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-0.5 tw-min-w-0 tw-w-full">
                    <span className="tw-truncate tw-min-w-0"><Typography variant="title-header" className="tw-text-ink-secondary">
                      Removed
                    </Typography></span>
                    <span className="tw-flex-shrink-0">{renderSortIcon('total_removed_count')}</span>
                  </span>
                </th>
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                  {/* Actions column - no header */}
                </th>
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                  {/* Expand column - no header icon */}
                </th>
              </tr>
            </thead>
          </table>
          </div>

          {/* Body — independent scroll container with the SAME colgroup as the
              header. scrollbar-gutter:stable on both sides ensures columns
              stay aligned regardless of whether the vertical scrollbar
              is visible. */}
          <div className="tw-flex-1" style={{ overflow: 'auto', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '4%' }} />
              <col style={{ width: '4%' }} />
            </colgroup>

            {/* Table Body */}
            <tbody
              /*
                Rows stay mounted through a refetch and DIM, instead of being swapped
                for a spinner row (§ Guide/ISR parity, 2026-08-31). Swapping emptied
                the table body on every page click, so the page height collapsed and
                snapped back and the row under the pointer moved — the change read as
                a reload rather than a page turn.

                `pointer-events-none` matters as much as the opacity: without it the
                stale rows stay clickable, and expanding row 3 of the OLD page while
                the new one lands is a real way to act on data that is no longer there.

                The spinner still shows on a COLD load, where there is nothing to dim.
              */
              aria-busy={isLoading || undefined}
              className={`tw-transition-opacity ${isLoading && rows.length > 0 ? 'tw-opacity-40 tw-pointer-events-none' : ''}`}
            >
              {/* Loading State */}
              {/* Cold load only — a refetch dims the rows above instead. */}
              {isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={12} className="tw-px-4 tw-py-12 tw-text-center">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-[3px]">
                      <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-edge tw-border-t-accent" />
                      <Typography variant="body-primary" className="tw-text-ink-secondary">Loading...</Typography>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map((row, index) => {
                const isEvenRow = index % 2 === 1;
                const cellBgClass = row.isSelected
                  ? ''
                  : `${isEvenRow ? 'tw-bg-surface-sunken' : 'tw-bg-surface'} group-hover:tw-bg-tone-neutral-tint`;
                const importedAttributes = row.attributes || [];

                return (
                  <React.Fragment key={row.id}>
                    {/* Main Row */}
                    <tr
                      className={`tw-group tw-border-b tw-border-edge tw-h-12 ${row.isSelected ? 'tw-bg-accent-tint' : 'hover:tw-bg-tone-neutral-tint'}`}
                    >
                      {/* Product Catalog Columns */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <Checkbox
                          label=""
                          checked={row.isSelected || false}
                          onChange={(checked) => handleRowSelect(index, checked)}
                          disabled={isEditMode}
                        />
                      </td>
                      <td className={`tw-px-2 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <Typography variant="title-header" className="tw-text-ink">
                          {(pagination.page - 1) * pagination.perPage + index + 1}
                        </Typography>
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`} title={row.catalog?.product || ''}>
                        <Typography variant="title" className="tw-text-ink-secondary">
                          {(row.catalog?.product || '').length > 11
                            ? `${(row.catalog?.product || '').slice(0, 11)}…`
                            : (row.catalog?.product || '')}
                        </Typography>
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                        <div className="tw-truncate" title={row.catalog?.category || ''}>
                          <Typography variant="title" className="tw-text-ink-secondary tw-truncate">
                            {(row.catalog?.category || '')}
                          </Typography>
                        </div>
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <Typography variant="title" className="tw-text-ink-secondary">
                          {row.catalog?.attributeCount || 0}
                        </Typography>
                      </td>

                      {/* Imported Products Columns */}
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden tw-border-l tw-border-edge ${cellBgClass}`} title={row.imported.product}>
                        <Typography variant="title-header" className="tw-text-ink tw-truncate">
                          {row.imported.product}
                        </Typography>
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                        <div className="tw-truncate" title={row.imported.category}>
                          <Typography variant="title-header" className="tw-text-ink tw-truncate">
                            {(row.imported.category || '')}
                          </Typography>
                        </div>
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <Status
                          tone={COUNTER_TONE.new}
                          label={row.imported.totalNewCount ?? 0}
                          data-testid={`catalog-new-count-${row.imported.sku}`}
                        />
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <Status
                          tone={COUNTER_TONE.update}
                          label={row.imported.totalUpdatesCount ?? 0}
                          data-testid={`catalog-update-count-${row.imported.sku}`}
                        />
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <Status
                          tone={COUNTER_TONE.removed}
                          label={row.imported.totalRemovedCount ?? 0}
                          data-testid={`catalog-removed-count-${row.imported.sku}`}
                        />
                      </td>
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <RowActionsMenu
                            actions={activeActions}
                            onAction={(actionType) => handleRowAction(index, actionType)}
                            disabled={!hasUpdatePermission || isEditMode}
                          />
                        </div>
                      </td>
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <button data-testid={`mpnupcmatchedview-mpnupcmatchedview-button-1-${index}`}
                            onClick={() => handleRowExpand(index)}
                            className="tw-p-1 tw-rounded hover:tw-bg-surface-hover-strong tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
                          >
                            {row.isExpanded ? <ArrowDown /> : <ArrowRight />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row Content */}
                    {row.isExpanded && (
                      <>
                        {/* Attribute Comparison */}
                        <ExpandedRowContent
                          colWidths={MPN_COL_WIDTHS}
                          catalogAttributes={row.catalogAttributes || []}
                          importedAttributes={row.attributes || []}
                          catalogMetadata={{
                            description: row.catalog?.description ?? '',
                            sku: row.catalog?.sku ?? '',
                            price: row.catalog?.price ?? '',
                            upc: row.catalog?.upc ?? '',
                            modelNumber: row.catalog?.modelNumber ?? '',
                            pls: row.catalog?.pls ?? '',
                            parentId: row.catalog?.parentId ?? '',
                            version: row.catalog?.version ?? '',
                            realSku: row.catalog?.realSku ?? '',
                          }}
                          importedMetadata={{
                            description: row.imported.description ?? '',
                            sku: row.imported.sku ?? '',
                            price: row.imported.price ?? '',
                            upc: row.imported.upc ?? '',
                            modelNumber: row.imported.modelNumber ?? '',
                            pls: row.imported.pls ?? '',
                            parentId: row.imported.parentId ?? '',
                            version: row.imported.version ?? '',
                            realSku: row.imported.realSku ?? '',
                            updatedFields: row.imported.updatedFields,
                          }}
                          isEditMode={isEditMode}
                          onAttributeChange={(attrIndex, value) =>
                            handleAttributeChange(index, attrIndex, value)
                          }
                          onDeleteAttribute={(attrIndex) =>
                            handleDeleteAttribute(index, attrIndex)
                          }
                          onDeleteAttributeValue={(attrIndex, valueIndex) =>
                            handleDeleteAttributeValue(index, attrIndex, valueIndex)
                          }
                          onRevertAttribute={(attrIndex) =>
                            handleRevertAttribute(index, attrIndex)
                          }
                          onRevertAttributeValue={(attrIndex, valueIndex) =>
                            handleRevertAttributeValue(index, attrIndex, valueIndex)
                          }
                          onRestoreAttributeValue={(attrIndex, valueIndex) =>
                            handleRestoreAttributeValue(index, attrIndex, valueIndex)
                          }
                          onUndoRestoreAttributeValue={(attrIndex, valueIndex) =>
                            handleUndoRestoreAttributeValue(index, attrIndex, valueIndex)
                          }
                          onModalAttrValuesSave={(attrName, valuesToKeep) =>
                            handleModalAttrValuesSave(index, attrName, valuesToKeep)
                          }
                          onEditModeEnter={() => {
                            if (!isEditMode) {
                              setIsEditMode(true);
                              onEditModeChange?.(true);
                              onRegisterEditHandlers?.({ save: () => saveRef.current(), cancel: () => cancelRef.current() });
                            }
                          }}
                          onMetadataChange={(field, value) =>
                            handleMetadataChange(index, field, value)
                          }
                          onAddAttributeValue={(attrIndex, value) =>
                            handleAddAttributeValue(index, attrIndex, value)
                          }
                          onAddNewAttribute={() => handleAddNewAttribute(index)}
                          onSelectNewAttributeName={(attrIndex, name) =>
                            handleSelectNewAttributeName(index, attrIndex, name)
                          }
                          loadValueOptions={loadValueOptionsForRow(index)}
                          loadAttributeOptions={() => loadAttributeOptionsForRow(index)}
                          onCreateNewValue={(attributeName) =>
                            setCreateValueModal({ rowIndex: index, attributeName })
                          }
                          onCreateNewAttribute={(attrIndex) =>
                            setCreateAttributeModal({ rowIndex: index, attrIndex })
                          }
                          productName={row.imported.product || row.catalog?.product || ''}
                        />

                        {/* Action Buttons Row - at BOTTOM per Figma 11660-14544 */}
                        <tr className="tw-border-b tw-border-edge tw-bg-surface">
                          {/* Product Catalog side - Search & Replace button or Product Replaced indicator */}
                          <td colSpan={5} className="tw-px-4 tw-py-3 tw-bg-surface">
                            {row.isReplaced ? (
                              <ProductReplacedIndicator onUndo={() => handleUndoReplace(index)} />
                            ) : (
                              <div className="tw-flex tw-justify-end">
                                <Button
                                  variant="primary"
                                  icon={<SearchIcon />}
                                  onClick={() => handleSearchReplace(index)}
                                >
                                  Search & Replace Product
                                </Button>
                              </div>
                            )}
                          </td>
                          {/* Imported Products side */}
                          <td colSpan={7} className="tw-px-4 tw-py-3 tw-bg-surface tw-border-l tw-border-edge" />
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {!isLoading && rows.length === 0 && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                No {activeSubTab === 'unmapped-sku' ? 'unmapped SKU' : 'unmapped SKU with updates'} products found
              </Typography>
            </div>
          )}
          </div>

          {/*
             The guard no longer includes `!isLoading`. It used to, and the strip therefore
             UNMOUNTED on every page click: the button vanished from under the pointer,
             keyboard focus fell to <body>, and the row it occupied collapsed so the page
             jumped. It stays mounted and takes `disabled` instead — which is what Guide/ISR's
             equivalent does.
          
             What remains here is `total > perPage`, and it stays at the CALL SITE: whether a
             table needs a pager at all is the view's question, not the bar's.
          */}
          {pagination.total > pagination.perPage && (
            <TablePaginationBar
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isLoading}
              data-testid="catalog-pagination-mpn-upc-matched"
            />
          )}
        </div>
      )}

      {/* Search & Replace Side Panel */}
      {searchReplacePanel.isOpen && searchReplacePanel.rowIndex !== null && (
        <SearchReplaceSidePanel
          isOpen={searchReplacePanel.isOpen}
          onClose={handleCloseSidePanel}
          channelKey={channelKey}
          sku={rows[searchReplacePanel.rowIndex].imported.sku || ''}
          importedProduct={{
            name: rows[searchReplacePanel.rowIndex].imported.product,
            category: rows[searchReplacePanel.rowIndex].imported.category,
            sku: rows[searchReplacePanel.rowIndex].imported.sku,
            price: rows[searchReplacePanel.rowIndex].imported.price,
            upc: rows[searchReplacePanel.rowIndex].imported.upc,
            modelNumber: rows[searchReplacePanel.rowIndex].imported.modelNumber,
            description: rows[searchReplacePanel.rowIndex].imported.description,
            pls: rows[searchReplacePanel.rowIndex].imported.pls,
            parentId: rows[searchReplacePanel.rowIndex].imported.parentId,
            version: rows[searchReplacePanel.rowIndex].imported.version,
            realSku: rows[searchReplacePanel.rowIndex].imported.realSku,
            attributes: rows[searchReplacePanel.rowIndex].attributes || [],
          }}
          onReplaceProduct={handleReplaceProduct}
          isReplacing={isReplacingProduct}
        />
      )}

      {/* Page-level LoadingOverlay only fires for view-wide ops (bulk
          action / edit-save). Per-surface ops (Replace, Create New)
          render their loading state inside their own panel/modal. */}
      {(isBulkActionLoading || isSaveLoading) && (
        <LoadingOverlay message={isEditMode ? 'Saving changes...' : 'Processing...'} />
      )}

      {/* Create as New Product Modal — entry point removed by ticket 11444,
          modal kept for re-introduction by future tickets. */}
      {createProductModal.rowIndex !== null && (
        <CreateNewProductModal
          isOpen={createProductModal.isOpen}
          onClose={handleCloseCreateProductModal}
          onSubmit={handleSubmitCreateProduct}
          csKey={channelKey}
          initialData={{
            productName: rows[createProductModal.rowIndex].imported.product,
            category: rows[createProductModal.rowIndex].imported.category,
            categoryUid: rows[createProductModal.rowIndex].imported.categoryId || '',
            sku: rows[createProductModal.rowIndex].imported.sku || '',
            realSku: rows[createProductModal.rowIndex].imported.realSku || '',
            price: rows[createProductModal.rowIndex].imported.price || '',
            upc: rows[createProductModal.rowIndex].imported.upc || '',
            modelNumber: rows[createProductModal.rowIndex].imported.modelNumber || '',
            pls: rows[createProductModal.rowIndex].imported.pls || 'Enabled',
            parentId: rows[createProductModal.rowIndex].imported.parentId || '',
            version: rows[createProductModal.rowIndex].imported.version || '',
            description: rows[createProductModal.rowIndex].imported.description || '',
            attributes: rows[createProductModal.rowIndex].attributes?.map(attr => ({
              name: attr.name,
              values: [attr.value],
            })) || [],
          }}
        />
      )}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
      />

      {/* Ignore Product Modal (reason chips + custom comment) — parity
          with Unmatched + SKU Matched. Single-row shows the product name;
          bulk shows an N-products summary. */}
      <IgnoreProductModal
        isOpen={showIgnoreModal}
        onClose={() => { setShowIgnoreModal(false); setPendingIgnoreSkus([]); clearSelection(); }}
        onSubmit={handleIgnoreProductSubmit}
        productName={
          pendingIgnoreSkus.length === 1
            ? rows.find((r) => r.imported.sku === pendingIgnoreSkus[0])?.imported.product
            : pendingIgnoreSkus.length > 1
              ? `${pendingIgnoreSkus.length} products selected`
              : undefined
        }
      />

      {/* 11443: consolidated delete-attribute confirmation. Triggered from
          handleSaveChanges when one or more pending-new attributes are still
          incomplete (no name, or no values). AC wording is verbatim. */}
      <ConfirmActionModal
        isOpen={deleteAttrConfirm.isOpen}
        onClose={closeDeleteAttrConfirm}
        onConfirm={deleteAttrConfirm.onConfirm}
        title="Delete attribute?"
        message={`You have not selected any values for: ${deleteAttrConfirm.names.join(', ')}. Do you want to delete ${deleteAttrConfirm.names.length === 1 ? 'this attribute' : 'these attributes'}?`}
        confirmLabel="Yes, delete"
        cancelLabel="No, go back"
      />

      {createValueModal && (
        <CreateAttributeValueModal
          isOpen
          onClose={() => setCreateValueModal(null)}
          onAdd={(values) => {
            const row = rows[createValueModal.rowIndex];
            const attrIdx = (row?.attributes || []).findIndex(
              (a) => a.name === createValueModal.attributeName,
            );
            if (attrIdx >= 0) {
              values.forEach((v) =>
                handleAddAttributeValue(createValueModal.rowIndex, attrIdx, v),
              );
            }
            setCreateValueModal(null);
          }}
          productName={rows[createValueModal.rowIndex]?.imported.product || ''}
          category={rows[createValueModal.rowIndex]?.imported.category || ''}
          attributeName={createValueModal.attributeName}
          csKey={channelKey}
          showInfoBar={false}
        />
      )}

      {createAttributeModal && (
        <CreateAttributeModal
          isOpen
          onClose={() => setCreateAttributeModal(null)}
          onCreate={(name) => {
            handleSelectNewAttributeName(
              createAttributeModal.rowIndex,
              createAttributeModal.attrIndex,
              name,
            );
            setCreateAttributeModal(null);
          }}
          productName={rows[createAttributeModal.rowIndex]?.imported.product || ''}
          category={rows[createAttributeModal.rowIndex]?.imported.category || ''}
          categoryUid={rows[createAttributeModal.rowIndex]?.imported.categoryId || ''}
          csKey={channelKey}
          existingAttributeNames={(rows[createAttributeModal.rowIndex]?.attributes || [])
            .map((a) => a.name)
            .filter((n) => n && !n.startsWith('__pending_'))}
          showInfoBar={false}
        />
      )}
    </div>
  );
};

export default MPNUPCMatchedView;
